import { getCookie } from '@alexis/helpers/cookie';
import { toISOStringWithTimezone } from '@alexis/helpers/date';
import { genzoTrack } from '@alexis/helpers/genzo';
import { generateCUID } from '@alexis/helpers/identity';
import { useFlightPLS } from '@pages/FlightSearchResult/contexts/FlightPerLegSearchContext/FlightPerLegSearchContext';
import { useFlightSearchResults } from '@pages/FlightSearchResult/contexts/FlightSearchResultsContext';
import useFlightSearchSort from '@pages/FlightSearchResult/hooks/useFlightSearchSort';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { getPriceOptionsState, getUserState } from '@redux/selectors';

import { FlightSelectedSortTypeForGenzoTracking } from '@wegoTypes/flights/flightSortType';

let scrollEventTimer: NodeJS.Timeout | null = null;
let filterEventTimer: NodeJS.Timeout | null = null;
let sortEventTimer: NodeJS.Timeout | null = null;
let pollEventTimer: NodeJS.Timeout | null = null;

const useFlightSearchViewGenzoTracking = ({
  selectedFiltersObject,
  pageViewId,
  visibleTripCards,
  wegoAnalyticsClientId,
  wegoAnalyticsClientSessionId,
}: {
  selectedFiltersObject: {
    [key: string]:
      | string
      | number
      | (string | number | undefined)[]
      | GenzoRangeOfSliderFilter
      | GenzoFlightTimeFilter
      | undefined;
  };
  pageViewId: string;
  visibleTripCards: TripViewModelDataForGenzo[];
  wegoAnalyticsClientId: string | undefined;
  wegoAnalyticsClientSessionId: string | undefined;
}) => {
  const { sortType } = useFlightSearchSort();
  const { search, sponsoredAdTripViewModels, tripViewModels } = useFlightSearchResults();
  const searchId = search?.id;

  const { showTotalPrice: isShowTotalPrice } = useSelector(getPriceOptionsState);
  const user = useSelector(getUserState);

  const { isPerLegSearch } = useFlightPLS();

  const [isGenzoTrackPolling, setIsGenzoTrackPolling] = useState<boolean>(false);
  const [isGenzoTrackFilter, setIsGenzoTrackFilter] = useState<boolean>(false);
  const [isGenzoTrackSort, setIsGenzoTrackSort] = useState<boolean>(false);

  const sortOrderTrackData = useMemo(() => {
    let selectedSortType = FlightSelectedSortTypeForGenzoTracking.SCORE;

    if (sortType === 'price') {
      selectedSortType = FlightSelectedSortTypeForGenzoTracking.PRICE;
    } else if (sortType === 'duration') {
      selectedSortType = FlightSelectedSortTypeForGenzoTracking.DURATION;
    } else if (sortType === 'score') {
      selectedSortType = FlightSelectedSortTypeForGenzoTracking.SCORE;
    } else {
      selectedSortType = FlightSelectedSortTypeForGenzoTracking.FLIGHT_TIME;
    }

    return selectedSortType;
  }, [sortType]);

  const genzoSearchViewTrackData = useMemo(() => {
    const clientData: any = {
      id: wegoAnalyticsClientId,
      session_id: wegoAnalyticsClientSessionId,
      ...(!!user && { user_hash: user.userHash }),
      wego_visitor_id: getCookie('wego_visitor_id'),
      wego_session_id: getCookie('wego_session_id'),
    };

    const searchViewData = {
      id: generateCUID(),
      created_at: toISOStringWithTimezone(new Date()),
      search_id: searchId,
      page_view_id: pageViewId,
      client: clientData,
      viewing_flights: visibleTripCards,
      selected_sort: JSON.stringify({ current_sort: sortOrderTrackData }),
      selected_filters: JSON.stringify(selectedFiltersObject),
      selected_price_option: isShowTotalPrice ? 'total_price' : 'per_person',
    };

    return searchViewData;
  }, [
    isShowTotalPrice,
    pageViewId,
    searchId,
    selectedFiltersObject,
    sortOrderTrackData,
    user,
    visibleTripCards,
    wegoAnalyticsClientId,
    wegoAnalyticsClientSessionId,
  ]);

  const genzoTrackFlightSearchView = useCallback(
    (trackData: Record<string, any>) => {
      if (!isPerLegSearch) {
        genzoTrack(`${API_BASE_URL}/genzo/v3/flights/search_view`, trackData);
      }
    },
    [isPerLegSearch],
  );

  const genzoTrackApplyFilterSearchView = useCallback(() => {
    if (filterEventTimer) {
      clearTimeout(filterEventTimer);
    }

    filterEventTimer = setTimeout(() => {
      setIsGenzoTrackFilter(true);
    }, 1000);
  }, []);

  const genzoTrackApplySortSearchView = useCallback(() => {
    if (sortEventTimer) {
      clearTimeout(sortEventTimer);
    }

    sortEventTimer = setTimeout(() => {
      setIsGenzoTrackSort(true);
    }, 1000);
  }, []);

  const genzoSearchViewOnScrollTracking = useCallback(() => {
    if (searchId && !!wegoAnalyticsClientId && !!wegoAnalyticsClientSessionId) {
      genzoTrackFlightSearchView({
        ...genzoSearchViewTrackData,
        action: 'SCROLL',
      });
    }
  }, [
    genzoSearchViewTrackData,
    genzoTrackFlightSearchView,
    searchId,
    wegoAnalyticsClientId,
    wegoAnalyticsClientSessionId,
  ]);

  const debouncedOnScrollTracking = useCallback(() => {
    if (scrollEventTimer) {
      clearTimeout(scrollEventTimer);
    }
    scrollEventTimer = setTimeout(() => {
      genzoSearchViewOnScrollTracking();
    }, 500);
  }, [genzoSearchViewOnScrollTracking]);

  const genzoTrackApplyPollingSearchView = useCallback(() => {
    if (pollEventTimer) {
      clearTimeout(pollEventTimer);
    }

    pollEventTimer = setTimeout(() => {
      setIsGenzoTrackPolling(true);
    }, 1000);
  }, []);

  useEffect(() => {
    if (isGenzoTrackFilter) {
      const filterTrackData = {
        ...genzoSearchViewTrackData,
        action: 'FILTER',
      };
      genzoTrackFlightSearchView(filterTrackData);

      setIsGenzoTrackFilter(false);
    }
  }, [isGenzoTrackFilter, genzoSearchViewTrackData]);

  useEffect(() => {
    if (isGenzoTrackSort) {
      genzoTrackFlightSearchView({
        ...genzoSearchViewTrackData,
        action: 'SORT',
      });

      setIsGenzoTrackSort(false);
    }
  }, [isGenzoTrackSort, genzoSearchViewTrackData]);

  useEffect(() => {
    if (isGenzoTrackPolling) {
      genzoTrackFlightSearchView({
        ...genzoSearchViewTrackData,
        action: 'POLLING',
      });

      setIsGenzoTrackPolling(false);
    }
  }, [isGenzoTrackPolling, genzoSearchViewTrackData]);

  useEffect(() => {
    window.addEventListener('scroll', () => {
      debouncedOnScrollTracking();
    });

    return () => {
      window.removeEventListener('scroll', () => {
        debouncedOnScrollTracking();
      });
    };
  }, [debouncedOnScrollTracking]);

  // Track Polling Search View
  useEffect(() => {
    if (tripViewModels.length > 0 || sponsoredAdTripViewModels.length > 0) {
      genzoTrackApplyPollingSearchView();
    }
  }, [genzoTrackApplyPollingSearchView, sponsoredAdTripViewModels.length, tripViewModels.length]);

  return {
    genzoTrackApplyFilterSearchView,
    genzoTrackApplySortSearchView,
  };
};

export default useFlightSearchViewGenzoTracking;
